import { apiRequest } from './httpClient';

// The marketing module keeps orchestration on the server: the browser only
// sends the draft and never decides which records are eligible to receive it.
export const getMarketingSnapshot = () => apiRequest('marketingGetSnapshot', { method: 'GET' });
export const listApprovedWhatsAppTemplates = () => apiRequest('marketingListWhatsAppTemplates', { method: 'GET' });

// Aunque la primera fuente sea SEO, este reporte pertenece a Mercadeo: junta
// todas las fuentes que terminan creando un lead/MQL.
export const getMarketingLeadAttributionReport = (days = 28) => apiRequest('getSeoLeadAttributionReport', {
  method: 'GET', query: { days },
});
export const getProspectingSnapshot = () => apiRequest('prospectingGetSnapshot', { method: 'GET' });
export const getProspectingSettings = () => apiRequest('prospectingGetSettings', { method: 'GET' });
export const saveProspectingSettings = (settings) => apiRequest('prospectingSaveSettings', { body: settings });
export const saveProspectingCampaign = (campaignId, campaign) => apiRequest('prospectingSaveCampaign', { body: { campaignId, campaign } });
export const runProspectingCampaign = (campaignId) => apiRequest('prospectingRunCampaign', { body: { campaignId } });
export const enrichProspectingCandidate = (candidateId) => apiRequest('prospectingEnrichCandidate', { body: { candidateId } });
export const backfillProspectingWebsiteEmails = ({ prospectingCampaignId, limit = 5, useHunterFallback = false } = {}) => apiRequest('prospectingBackfillWebsiteEmails', { body: { prospectingCampaignId, limit, useHunterFallback } });
export const saveProspectingManualContact = ({ candidateId, email, contactName, sourceNote }) => apiRequest('prospectingSaveManualContact', { body: { candidateId, email, contactName, sourceNote } });
export const updateProspectingCandidateTags = ({ candidateId, tags }) => apiRequest('prospectingUpdateCandidateTags', { body: { candidateId, tags } });
export const setProspectingCandidateStatus = (candidateId, status) => apiRequest('prospectingSetCandidateStatus', { body: { candidateId, status } });
export const addProspectingCandidateToAudience = ({ candidateId, campaignId }) => apiRequest('marketingAddProspectingCandidateToAudience', { body: { candidateId, campaignId } });
export const activateProspectingEmailCandidate = ({ candidateId, campaignId }) => apiRequest('prospectingActivateEmailCandidate', { body: { candidateId, campaignId, consentConfirmed: true } });
export const sendProspectingWhatsAppTemplate = ({ candidateId, templateKey, message }) => apiRequest('prospectingSendWhatsAppTemplate', { body: { candidateId, templateKey, message } });

export const listMarketingFlows = () => apiRequest('listMarketingFlows', { method: 'GET' });

export const getMarketingFlow = (flowId) => apiRequest('getMarketingFlow', {
  method: 'GET',
  query: { flowId },
});

export const saveMarketingFlow = (flow) => apiRequest('saveMarketingFlow', {
  body: { flow },
});

export const previewColdLeadCampaign = (criteria) => apiRequest('previewColdLeadCampaign', {
  body: { criteria },
});

export const prepareColdLeadCampaign = (campaign) => apiRequest('prepareColdLeadCampaign', {
  body: { campaign },
});

export const previewMarketingImport = (rows) => apiRequest('marketingImportPreview', {
  body: { rows },
});

// Impulso solo prepara texto para el editor. Nunca crea, lanza ni envía una
// campaña: el equipo revisa el borrador antes de guardarlo.
export const generateMarketingCampaignDraft = (request) => apiRequest('generateMarketingCampaignDraft', {
  body: request,
});

export const createMarketingCampaign = (campaign) => apiRequest('marketingCreateCampaign', {
  body: { campaign },
});

export const updateMarketingCampaign = (campaignId, campaign) => apiRequest('marketingUpdateCampaign', {
  body: { campaignId, campaign },
});
export const getMarketingCampaignAudience = (campaignId) => apiRequest('marketingGetCampaignAudience', { method: 'GET', query: { campaignId } });

export const launchMarketingCampaign = (campaignId) => apiRequest('marketingLaunchCampaign', {
  body: { campaignId },
});

// El worker está limitado y se invoca de manera explícita: preparar una
// campaña nunca dispara envíos por sorpresa desde el navegador.
export const processMarketingCampaign = (campaignId, { dryRun = false, limit = 25 } = {}) => apiRequest('processMarketingCampaignQueue', {
  body: { campaignId, dryRun, limit },
});

// Manda el email tal cual saldría en la campaña real, solo a un correo de
// prueba — no toca la audiencia ni cuenta como enviado.
export const sendMarketingCampaignTest = ({ campaignId, testEmail, testName }) => apiRequest('sendMarketingCampaignTest', {
  body: { campaignId, testEmail, testName },
});

// Files are intentionally sent to the authenticated function. It is the
// function—not the client—that chooses the final Storage path and asset URL.
export const uploadMarketingAsset = (asset) => apiRequest('marketingUploadAsset', {
  body: { asset },
});

// Operations used by the reporting/remarketing layer. Queueing defaults to a
// dry-run on the server; callers must deliberately pass `dryRun: false` after
// reviewing the returned candidates.
export const getMarketingAnalytics = (campaignId, limit) => apiRequest(
  `getMarketingAnalytics?campaignId=${encodeURIComponent(campaignId)}${limit ? `&limit=${encodeURIComponent(limit)}` : ''}`,
  { method: 'GET' }
);

export const queueMarketingRemarketing = (request) => apiRequest('queueMarketingRemarketing', {
  body: request,
});

// Public endpoints: the opaque enrollment token authorizes only the recipient
// to answer their campaign questionnaire. They never receive an admin token.
export const getCampaignInterestWizard = (campaignId, token) => apiRequest('getCampaignInterestWizard', {
  method: 'GET', query: { campaignId, token }, requireAuth: false,
});

export const submitCampaignInterestWizard = ({ campaignId, token, answers }) => apiRequest('submitCampaignInterestWizard', {
  body: { campaignId, token, answers }, requireAuth: false,
});

// Standalone qualification link (createMarketingQualificationLink), used
// outside of a campaign send — e.g. a one-off link an admin hands a lead.
export const getPublicQualificationSession = (token) => apiRequest('getPublicQualificationSession', {
  method: 'GET', query: { token }, requireAuth: false,
});

export const submitPublicQualification = ({ token, answers }) => apiRequest('submitPublicQualification', {
  body: { token, answers }, requireAuth: false,
});

export const listMarketingCampaignEnrollments = (campaignId, limit) => apiRequest('listMarketingCampaignEnrollments', {
  method: 'GET', query: { campaignId, limit },
});

// Agrega una lista a una campaña que ya existe (en vez de crear una nueva).
export const addContactsToCampaign = ({ campaignId, importContacts }) => apiRequest('addContactsToCampaign', {
  body: { campaignId, importContacts },
});
